"use client";

import Image from "next/image";
import { ScanLine, TriangleAlert } from "lucide-react";
import { useEffect, useRef, useState, type CSSProperties } from "react";

import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

import type { AxScanCopy } from "./annonsrontgen-copy";

/** Seconds the beam takes to pass the table, and how long a channel stays up. */
const SCAN_SECONDS = 3.2;
const CHANNEL_SECONDS = 9;

type Platform = AxScanCopy["platforms"][number];
type Row = Platform["rows"][number];

/**
 * The ad X-ray hero: a realistic ad account where a scanning beam passes the
 * rows and the leaks light up one by one, with a counter for the share of
 * budget that never brought a sale. Hover (or touch) turns the pointer into
 * an X-ray lens over the account; the channels rotate on their own.
 */
export function CcAxScanner({ copy }: { copy: AxScanCopy }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const reduced = useCcPrefersReducedMotion();
  const [index, setIndex] = useState(0);
  const [scanned, setScanned] = useState(0);
  const [lens, setLens] = useState(false);
  const [inView, setInView] = useState(false);
  const platform = copy.platforms[index];
  const leaks = platform.rows.filter((row) => "leak" in row && row.leak).length;
  const done = scanned >= platform.rows.length;

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      {
        threshold: 0.3,
      },
    );
    observer.observe(root);
    return () => observer.disconnect();
  }, []);

  // Rows count as scanned as the beam passes them.
  useEffect(() => {
    if (!inView) return;
    const total = platform.rows.length;
    if (reduced) {
      const timer = window.setTimeout(() => setScanned(total), 0);
      return () => window.clearTimeout(timer);
    }
    const step = (SCAN_SECONDS * 1000) / total;
    const timers = platform.rows.map((_, row) =>
      window.setTimeout(() => setScanned(row + 1), 250 + step * (row + 0.6)),
    );
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [index, inView, platform.rows, reduced]);

  // Next channel after a while, unless the visitor is looking through the lens.
  useEffect(() => {
    if (!inView || lens || reduced) return;
    const timer = window.setTimeout(() => {
      setScanned(0);
      setIndex((current) => (current + 1) % copy.platforms.length);
    }, CHANNEL_SECONDS * 1000);
    return () => window.clearTimeout(timer);
  }, [index, inView, lens, reduced, copy.platforms.length]);

  const pick = (next: number) => {
    if (next === index) return;
    setScanned(0);
    setIndex(next);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    event.currentTarget.style.setProperty(
      "--mx",
      `${event.clientX - box.left}px`,
    );
    event.currentTarget.style.setProperty(
      "--my",
      `${event.clientY - box.top}px`,
    );
  };

  return (
    <div
      ref={rootRef}
      className="cc-ax-scan"
      data-done={done}
      style={{ "--scan": `${SCAN_SECONDS}s` } as CSSProperties}
    >
      <div className="cc-ax-scan__window">
        <header className="cc-ax-scan__bar">
          <i className="cc-ax-scan__dots" aria-hidden="true">
            <b />
            <b />
            <b />
          </i>
          <span className="cc-ax-scan__app">
            <Image
              src={platform.logo}
              alt=""
              width={16}
              height={16}
              unoptimized
            />
            {platform.name}
          </span>
          <em>{copy.period}</em>
        </header>

        <div className="cc-ax-scan__account">
          <span>{platform.account}</span>
          <span className="cc-ax-scan__status" data-done={done}>
            <ScanLine size={14} strokeWidth={2.2} aria-hidden="true" />
            {done ? `${leaks} ${copy.found}` : copy.scanning}
          </span>
        </div>

        <div
          className="cc-ax-scan__stage"
          data-lens={lens}
          onPointerEnter={(event) => {
            if (event.pointerType === "mouse") setLens(true);
          }}
          onPointerLeave={() => setLens(false)}
          onPointerMove={onPointerMove}
        >
          <AccountTable
            key={`ui-${index}`}
            platform={platform}
            scanned={scanned}
          />
          <div
            key={`xr-${index}`}
            className="cc-ax-scan__xray"
            aria-hidden="true"
          >
            <AccountTable
              platform={platform}
              scanned={platform.rows.length}
              xray
            />
          </div>
          {done || reduced ? null : (
            <span
              key={`beam-${index}`}
              className="cc-ax-scan__beam"
              aria-hidden="true"
            />
          )}
          {lens ? (
            <span className="cc-ax-scan__lens" aria-hidden="true" />
          ) : null}
        </div>

        <footer className="cc-ax-scan__summary">
          <p>
            <b>
              <Counter value={done ? platform.leak : "0 %"} />
            </b>
            <span>{copy.leakLabel}</span>
          </p>
          <span className="cc-ax-scan__hint">{copy.hint}</span>
        </footer>
      </div>

      <div
        className="cc-ax-scan__tabs"
        role="tablist"
        aria-label={copy.tabsLabel}
      >
        {copy.platforms.map((item, tab) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === index}
            className="cc-ax-scan__tab"
            onClick={() => pick(tab)}
          >
            <Image src={item.logo} alt="" width={16} height={16} unoptimized />
            {item.name}
            {tab === index && !lens && !reduced ? (
              <i
                key={`p-${index}`}
                className="cc-ax-scan__progress"
                aria-hidden="true"
              />
            ) : null}
          </button>
        ))}
      </div>
      <p className="cc-ax-scan__example">{copy.example}</p>
    </div>
  );
}

function AccountTable({
  platform,
  scanned,
  xray = false,
}: {
  platform: Platform;
  scanned: number;
  xray?: boolean;
}) {
  return (
    <table className="cc-ax-table" data-xray={xray}>
      <thead>
        <tr>
          {platform.columns.map((column) => (
            <th key={column} scope="col">
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {platform.rows.map((row: Row, index) => {
          const leak = "leak" in row ? row.leak : undefined;
          const found = Boolean(leak) && index < scanned;
          return (
            <tr
              key={row.label}
              data-leak={Boolean(leak)}
              data-found={found}
              style={{ "--i": index } as CSSProperties}
            >
              <th scope="row">
                <span className="cc-ax-table__term">{row.label}</span>
                {/* Leak rows always keep the flag line, so both layers line up. */}
                {leak ? (
                  <span className="cc-ax-table__flag" data-show={found || xray}>
                    <TriangleAlert
                      size={12}
                      strokeWidth={2.4}
                      aria-hidden="true"
                    />
                    <span>{xray ? leak.text : leak.tag}</span>
                  </span>
                ) : null}
              </th>
              <td>{row.cost}</td>
              <td>{row.conv}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** Ticks a "23 %" style value up from zero. */
function Counter({ value }: { value: string }) {
  const target = Number.parseFloat(value.replace(",", "."));
  const suffix = value.replace(/^[\d.,\s]+/, "");
  const [shown, setShown] = useState(0);

  useEffect(() => {
    if (!Number.isFinite(target)) return;
    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / 900);
      setShown(Math.round(target * (1 - Math.pow(1 - t, 3))));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target]);

  return (
    <>
      {shown}
      {suffix ? ` ${suffix.trim()}` : null}
    </>
  );
}
